/* eslint-disable complexity */
import React, { FC, useCallback, useMemo, useState } from 'react';
import { GetPricesAndFeesDto, priceFormatter } from '@sweatpals/shared';
import { Box, Button, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { sortedUniqBy } from 'lodash-es';
import { Showable, useStyles } from '@sweatpals/react-ui-kit';
import { StripeElements } from '@stripe/stripe-js';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import Image from 'next/legacy/image';
import { CheckIcon } from '../../icons';
import { CheckoutFormDto, DonationTierDto, DonationTierEnum } from '../../../interfaces/checkout-form.dto';
import { CoreNumberInput } from '../core-number-input';
import { CoreFormControl } from '../core-form-control';
import { CoreTextField } from '../core-textfield';
import { stripeService } from '../../../services';
import { checkoutFromSubmitButtonId, defaultStripeElementsOptions } from '../../../constants';
import loader from '../../../public/assets/images/loader.gif';
import ReCaptchaBackgroundVerifier from '../re-captcha/re-captcha-background.component';
import { EventType } from '../../../services/types/event-types';
import { commonCheckoutFormStyles } from './checkout-form.styles';
import { paymentStepStyles } from './payment-step.styles';
import { useAnalytics } from 'hooks/use-analytics.hook';

export interface PaymentStepProps {
	eventData: EventType;
	minDonation: number;
	ticketsQuantity: number;
	promocode?: string;
	isPromocodeApplied?: boolean;
	donationAmount?: number;
	isPaying?: boolean;
	isE2ETest?: boolean;
	isPaymentRequired?: boolean;
	priceAndFees?: GetPricesAndFeesDto | null;
	isPolicyAgreed?: boolean;
	isPriceAndFeesLoading?: boolean;
	showRecaptchaCheckbox?: boolean;
	cancelFunc(): void;
	setDonationAmount(value: number): void;
	setPromocode(value: string): void;
	getPriceAndFees(promocode?: string): Promise<GetPricesAndFeesDto>;
	debounceFunc(func: { (): Promise<void> | void }): Promise<void>;
	handlePay(elements: StripeElements, isVerificationRequired?: boolean): Promise<void>;
	handleMockPayForE2E(): Promise<void>;
}

// eslint-disable-next-line complexity
export const PaymentStep: FC<PaymentStepProps> = props => {
	const {
		eventData,
		minDonation,
		promocode,
		priceAndFees,
		ticketsQuantity,
		donationAmount,
		isPaying = false,
		isE2ETest = false,
		isPromocodeApplied = false,
		isPaymentRequired = false,
		isPolicyAgreed = false,
		isPriceAndFeesLoading = false,
		showRecaptchaCheckbox = false,
		cancelFunc,
		debounceFunc,
		setPromocode,
		getPriceAndFees,
		setDonationAmount,
		handlePay,
		handleMockPayForE2E,
	} = props;

	const {
		author,
		additionalData: additionalEventData,
	} = eventData;

	const [selectedDonationTierId, setSelectedDonationTierId] = useState<DonationTierEnum>(
		minDonation > 0 ? DonationTierEnum.CustomPrice : DonationTierEnum.HigherPrice
	);
	const [donationError, setDonationError] = useState<string | null>(null);
	const [isPromocodeEnabled, setIsPromocodeEnabled] = useState(false);
	const [isPromocodeAppling, setIsPromocodeAppling] = useState(false);
	const [promocodeError, setPromocodeError] = useState('');
	const { logEventTyped } = useAnalytics();

	const commonStyles = useStyles(commonCheckoutFormStyles, additionalEventData?.customWebCheckoutMainColor);
	const styles = useStyles(paymentStepStyles);

	const donationTiers: DonationTierDto[] = useMemo(
		() => [
			...sortedUniqBy(
				[
					{ id: DonationTierEnum.HigherPrice, amount: 2000 },
					{ id: DonationTierEnum.MediumPrice, amount: 1000 },
					{ id: DonationTierEnum.LowPrice, amount: minDonation || 500 },
				].filter(donation => donation.amount >= minDonation),
				v => v.amount
			),
			{ id: DonationTierEnum.CustomPrice, amount: minDonation || 0 }
		],
		[minDonation]
	);

	const {
		isSubmitting,
		setFieldValue,
	} = useFormikContext<CheckoutFormDto>();

	const handleSetRecaptchaV2Token = useCallback(
		(token: string | null) => {
			setFieldValue('recaptchaTokenV2', token);
			setFieldValue('recaptchaTokenV3', null);
		},
		[setFieldValue]
	);

	const handleSetRecaptchaV3Token = useCallback(
		(token: string | null) => {
			setFieldValue('recaptchaTokenV3', token);
			setFieldValue('recaptchaTokenV2', null);
		},
		[setFieldValue]
	);

	const handleChangeDonationAmount = useCallback((amount: number) => {
		if (amount >= minDonation) {
			debounceFunc(async (): Promise<void> => {
				await getPriceAndFees(isPromocodeApplied ? promocode : undefined);
			});
			setDonationError(null);
			setDonationAmount(amount);
		} else {
			setDonationError(`Minimum donation amount for this event is ${priceFormatter(minDonation)}`);
			setDonationAmount(minDonation);
		}
	}, [minDonation, debounceFunc, setDonationAmount, getPriceAndFees, isPromocodeApplied, promocode]);

	const onDonationTierClick = useCallback((tier: DonationTierDto) => {
		setSelectedDonationTierId(tier.id);
		handleChangeDonationAmount(tier.amount);
	}, [handleChangeDonationAmount]);

	const applyPromocode = useCallback(async () => {
		setIsPromocodeAppling(true);
		setPromocodeError('');

		try {
			cancelFunc();
			const pricesAndFees = await getPriceAndFees(promocode);

			const isPromocodeValid = pricesAndFees?.discountIds?.length > 0;

			logEventTyped('promo_code_applied', {
				promoCode: promocode,
				discountAmount: pricesAndFees?.discountAmount,
				totalAmount: pricesAndFees?.fullPrice,
				promoCodeStatus: isPromocodeValid ? 'valid' : 'invalid',
			});

			if (!isPromocodeValid) {
				setPromocodeError('Promocode not found');
			}
		} catch (error) {
			setPromocodeError('An error occurred while applying the promocode');
		} finally {
			setIsPromocodeAppling(false);
		}
	}, [cancelFunc, getPriceAndFees, promocode, logEventTyped]);

	return (
		<Box display="flex" flexDirection="column" gap="24px">
			<Showable show={eventData.isPaid}>
				<Showable show={eventData.isDonationBased}>
					<Box display="flex" flexDirection="column" gap="8px">
						<Box display="flex" flexDirection="column" gap="4px">
							<Typography sx={commonStyles.title}>Donation</Typography>
							<Typography sx={styles.donationDescription}>
								This is a donation based event. {' '}
								{minDonation
									? `Minimal donation is ${priceFormatter(minDonation)}`
									: 'Select your donation amount to support the host'}
							</Typography>
						</Box>
						<Box display="flex" gap="8px">
							{donationTiers.map(tier => (
								<Button
									key={tier.id}
									sx={styles.donationTier(selectedDonationTierId === tier.id)}
									disabled={isSubmitting || isPaying}
									onClick={() => {
										onDonationTierClick(tier);
									}}
								>
									<Showable show={selectedDonationTierId === tier.id}>
										<CheckIcon />
									</Showable>
									{tier.id === DonationTierEnum.CustomPrice ? 'Custom' : priceFormatter(tier.amount)}
								</Button>
							))}
						</Box>
						<Showable show={selectedDonationTierId === DonationTierEnum.CustomPrice}>
							<CoreNumberInput
								fullWidth
								id="donation-amount-input"
								value={donationAmount ? (donationAmount / 100) : 0}
								disabled={isSubmitting || isPaying}
								helperText={donationError}
								error={!!donationError}
								decimalScale={2}
								startAdornment={<Box sx={{ color: isSubmitting ? '#FFFFFF99' : '#FFFFFF' }}>$</Box>}
								isAllowed={({ floatValue }) => !floatValue || (floatValue >= 0 && floatValue <= 999.99)}
								onChange={value => {
									handleChangeDonationAmount(value && (value * 100));
								}}
								onBlur={e => {
									if (!e.target.value) {
										handleChangeDonationAmount(minDonation);
									}
								}}
								allowNegative={false}
								min={minDonation || 0}
							/>
						</Showable>
					</Box>
				</Showable>
				<Showable hide={eventData.isDonationBased}>
					<Box display="flex" alignItems="center" justifyContent="space-between">
						<Typography sx={commonStyles.title}>Payment</Typography>
						<Showable show={!isPromocodeEnabled}>
							<Button
								id="show-promo-code-input-button"
								sx={commonStyles.secondaryButton}
								disabled={isSubmitting || isPaying}
								onClick={() => { setIsPromocodeEnabled(true); }}
							>
								Apply promocode
							</Button>
						</Showable>
					</Box>
					<Showable show={isPromocodeEnabled}>
						<CoreFormControl label="Promo Code">
							<Box display="flex" alignItems="flex-start" gap="13px">
								<CoreTextField
									id="promo-code-input"
									value={promocode}
									placeholder="Enter promo code"
									sx={{
										'& .MuiInputBase-input': {
											textTransform: 'uppercase',
										}
									}}
									controlSx={{ flex: 1 }}
									disabled={
										isSubmitting
										|| isPromocodeApplied
										|| isPromocodeAppling
										|| isPaying
									}
									onChange={e => {
										setPromocode(e.target.value?.toLowerCase());
										setPromocodeError('');
									}}
									error={!!promocodeError}
									helperText={promocodeError}
								/>
								{isPromocodeApplied ? (
									<Box id="promo-code-applied" sx={styles.appliedPromocode}>
										<CheckIcon/>
										Applied
									</Box>
								) : (
									<Button
										id="apply-promo-code-button"
										sx={styles.applyPromocodeButton}
										disabled={
											isSubmitting
											|| isPaying
											|| isPromocodeAppling
										}
										onClick={applyPromocode}
									>
										Apply
									</Button>
								)}
							</Box>
						</CoreFormControl>
					</Showable>
				</Showable>
			</Showable>
			<Showable show={isPaymentRequired}>
				<Box position="relative">
					<Showable hide={isE2ETest}>
						<Elements
							stripe={stripeService.stripe}
							options={{
								...defaultStripeElementsOptions,
								amount: priceAndFees?.fullPrice ?? 50,
								onBehalfOf: author?.stripeConnectedAccountId,
							}}
						>
							<ElementsConsumer>
								{({ elements }) => (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '24px',
											opacity: isPaying ? 0.2 : 1
										}}
									>
										<PaymentElement
											id="payment-element"
											options={{
												layout: 'tabs',
											}}
										/>
										<Box sx={{ display: 'flex', justifyContent: 'center' }}>
											<Button
												id={checkoutFromSubmitButtonId}
												sx={commonStyles.submitButton}
												disabled={
													!isPolicyAgreed
													|| !ticketsQuantity
													|| !!donationError
													|| isPriceAndFeesLoading
												}
												onClick={() => {
													handlePay(elements);
												}}
											>
												Pay with card
											</Button>
										</Box>
									</Box>
								)}
							</ElementsConsumer>
						</Elements>
					</Showable>
					<Showable show={isE2ETest}>
						<Box sx={{ display: 'flex', justifyContent: 'center' }}>
							<Button
								id={checkoutFromSubmitButtonId}
								sx={commonStyles.submitButton}
								disabled={
									!isPolicyAgreed
									|| !ticketsQuantity
									|| !!donationError
									|| isPriceAndFeesLoading
								}
								onClick={() => {
									handleMockPayForE2E();
								}}
							>
								Pay with card
							</Button>
						</Box>
					</Showable>
					<Showable show={isPaying}>
						<Box sx={commonStyles.loader}>
							<Image src={loader} width={64} height={64} alt="google-play" />
						</Box>
					</Showable>
				</Box>
			</Showable>

			{/* <Showable show={!isVerificationRequired}> */}
			{/* {() => ( */}
			<Box>
				<ReCaptchaBackgroundVerifier
					showCheckbox={showRecaptchaCheckbox}
					onVerifyV2={handleSetRecaptchaV2Token}
					onVerifyV3={handleSetRecaptchaV3Token}
				/>
			</Box>
			{/* )} */}
			{/* </Showable> */}
		</Box>
	)
}
